import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useLocation } from "react-router-dom"
import { useDispatch } from 'react-redux'

import { Drawer, DrawerContent, DrawerItem, ContextMenu, MenuItem } from '@progress/kendo-react-layout';

import MainAppBarView from 'views/main/MainAppBarView.jsx';
import LoadingPanel from 'views/widgets/LoadingPanel.jsx';
import UnsavedChangesDialog from 'views/widgets/UnsavedChangesDialog.jsx';
import FiltersDialog from 'views/widgets/FiltersDialog.jsx';
import { navItems } from './navItems.js';
import { navItemsMobile } from './navItemsMobile.js';
import useSession from '../../hooks/useSession';
import { addMessage } from '../../messages/messageSlice'
import dirtyHandler from '../../constituo/dirtyHandler';

export default function ConstituoView(props) {

	const path = useLocation().pathname;
	const { messageInfo } = props;
	const { getPreference, setPreference, persistPreferences, hasMenuType, isWorkspaceAdmin } = useSession();
	const { isDirty, setDirty } = dirtyHandler();  
	const navigate = useNavigate();
	const matches = window.matchMedia("(min-width: 768px)").matches;
	const initExpanded = matches && (getPreference("LEFT_NAV_STATUS") == "expanded");
	const [filterState, setFilterState] = useState({ show: false, filters: getPreference("MENU_FILTERS"), searching: false });
	const [menuState, setMenuState] = useState({ expanded: initExpanded, temporarilyExpanded: false });
	const defaultItems = matches ? navItems : navItemsMobile;
	const [items, setItems] = useState(defaultItems);
	const [sessionChanging, setSessionChanging] = useState(false);
	const [unsavedChangesDialogOptions, setUnsavedChangesDialogOptions] = useState(false);
	const dispatch = useDispatch();

	const [showContextMenu, setShowContextMenu] = React.useState(false);
	const offset = React.useRef({
		left: 0,
		top: 0,
		uri: '',
	});
	
	const applyFilters = (newFilters) => {
		const options = {
			credentials: 'include',
			method: "POST",
			headers: { 'Content-Type': 'application/json' },
			body: newFilters
		};

		setFilterState({...filterState, show: false, searching: true, filters: newFilters});
		fetch(API_URL + '/api/attributes/filter/?_=' + new Date().getTime(), options
		).then((response) => {
			if (response.ok) { return response.json(); }
			else { throw new Error(response.status); }
		}).then((json) => {
			setFilterState({...filterState, show: false, searching: false, filters: newFilters});
			updateMenuItems(defaultItems.map((item) => item.id === 'filtered' ? {...item, expanded: true} : item).concat(json));
		}).catch((err) => {
			setFilterState({...filterState, show: false, searching: false, filters: newFilters});
			let errMsg = (err.message === '403') ? ("Unauthorized session!") : ("Unable to filter.  If this problem persists, please contact Support.");
			updateMenuItems(items);
			dispatch(addMessage({ type: 'error', msg: errMsg }));
			if (err.message === '403') window.location = window.location.origin + '/constituo/';
		});
	};
	
	const updateMenuItems = (menuItems) => {
		const newItems = menuItems.map((item) => {			
			if(item.id === 'workspace')
				item.route = '/workspace/view/' + isWorkspaceAdmin() + '/';
			return item;
		});
		
		setItems(newItems);
		
		if (!matches) dispatch(addMessage({ 
			type: 'warning', 
			displayOverride: true, 
			msg: "Some features may not be available on mobile devices.  Constituo Content is best viewed on desktop devices." 
		}));	
	};

	const filtersUpdated = (newFilters) => {
		setPreference("MENU_FILTERS", newFilters);
		setFilterState({ show: false, filters: newFilters });
		if (JSON.parse(newFilters).length > 0)
			applyFilters(newFilters);
		else
			updateMenuItems(defaultItems);
	};
	
	const findItem = (id) => {
		let found = items.find((item) => item.id == id);
		return found;
	};
	
	const findItemsByParent = (parentId) => {
		let found = items.filter((item) => {
			return item.parentId == parentId;
		});
		return found;
	};
	
	const handleContextMenu = (e) => {	
		e.preventDefault();
		const route = e.target.getAttribute("route") || '';
		
		offset.current = {
			left: e.pageX,
			top: e.pageY,
			route: route,
		};
    	
    	setShowContextMenu(true);  
  	};	
  	
	const handleOnSelectContextMenu = (e) => {		
		const action = e.item.data.action;
		const { route } = offset.current; 
		const url = window.location.origin + '/constituo/#' + route;

		setShowContextMenu(false);
	
		switch (action) {
			case 'new_tab':
				window.open(url, '_blank', 'noreferrer');
				break;
			case 'new_window':
				window.open(url, '_blank', 'location=0');
				break;
			case 'copy_link':
				navigator.clipboard.writeText(url);
				break;
			default:	
				break;
		}
	};
  	
	const handleOnCloseContextMenu = () => {
		setShowContextMenu(false);
	}; 
	
	const handleNavToggle = () => {
		let newExpandedState = menuState.expanded ? false : 
		                       menuState.temporarilyExpanded ? false :
		                       true;
		setPreference("LEFT_NAV_STATUS", newExpandedState ? "expanded" : "collapsed");
		setMenuState({ expanded: newExpandedState, temporarilyExpanded: false });
	};
	
	const navigateTo = (url) => {
		checkForUnsavedChanges({ route: url });
	}; 	
	
	const handleFilterClicked = (e) => {
		setFilterState({ ...filterState, show: true });
		
		// prevent triggering the menu select event when clicking the button
		e.preventDefault();
		e.stopPropagation();
	}
	
	const handleFiltersCleared = (e) => {
		setPreference("MENU_FILTERS", "[]");
		setFilterState({ ...filterState, show: false, filters: "[]" });
		updateMenuItems(defaultItems);
		
		// prevent triggering the menu select event when clicking the button
		e.preventDefault();
		e.stopPropagation();
	};
	
	const handleSelect = (event) => {
		const selectedItem = findItem(event.itemTarget.props.id);
		const selectedItemIsAParent = selectedItem.expanded != undefined;

    	const newData = items.map((item) => {
			const { selected, expanded, id, ...others } = item;
			const isSelectedItem = selectedItem.id === item.id
			const thisIsAParent = item.expanded != undefined;

			if (thisIsAParent) {
				return {
					id: id,
					selected: isSelectedItem,
					expanded: (isSelectedItem ? (menuState.expanded || menuState.temporarilyExpanded ? !selectedItem.expanded : true)
							: selectedItemIsAParent ? false
							: item.expanded),
					...others
				};
			} else {
				
				return {
					id: id,
					selected: isSelectedItem,
					...others
				};
			}
		});

    	setItems(newData);
		setMenuState({ ...menuState, temporarilyExpanded: selectedItemIsAParent });
		
		checkForUnsavedChanges(selectedItem);
	}
	
	const checkForUnsavedChanges = (selectedItem) => {
    	if (!selectedItem.route) return;

    	const viewer = window.currentViewerRef;
    	const pdfDirty =
        	viewer?.getIsDocumentDirty &&
        	viewer.getIsDocumentDirty();

    	if (isDirty() || pdfDirty) {
	        setUnsavedChangesDialogOptions({
            	show: true,
            	newRoute: selectedItem.route
        	});
    	} else {
        	navigate(selectedItem.route);
        	dispatch(addMessage({
            	type: 'info',
            	msg: 'Navigated to ' + selectedItem.title,
            	hidden: true
        	}));
    	}
	};
	
	const unsavedChangesConfirmed = () => {
		let newRoute = unsavedChangesDialogOptions.newRoute;
		setDirty(() => { return false });
		setUnsavedChangesDialogOptions({ show: false });
		navigate(newRoute);
	};
	
	const isMenuItemVisible = (menuItem) => {
		let visible = menuItem.visible;
						
		// handle collapsed menu
		if (!menuState.expanded && !menuState.temporarilyExpanded && menuItem.parentId !== undefined) return false;

		return hasMenuType(menuItem, visible);
	};
	
	const contextMenu = () => {		
		return (
			<ContextMenu
				vertical={true}
				onSelect={handleOnSelectContextMenu}
				show={showContextMenu}
				offset={offset.current}
				onClose={handleOnCloseContextMenu}
			>
				<MenuItem 
					text="Open link in new tab"
					data={{
						action: "new_tab"
					}} 
				/>
				<MenuItem 
					text="Open link in new window"
					data={{
						action: "new_window"
					}}	
				/>
				<MenuItem
					text="Copy link address"
					data={{
						action: "copy_link"
					}}
				/>
			</ContextMenu>	
		)			
	};	
	
	const menuItemClicked = (e, id) => {
		if (id === "help")
			console.log("Help");
		else if (id === "logoff")
			persistPreferences();
	};
	
	const isNavExpanded = menuState.expanded || menuState.temporarilyExpanded;
	
	const customItem = (props) => {
		const { visible, expanded, parentId, orgAdminAllowed, workspaceAdminAllowed, requiresWorkspace, requiresInstance, requiresConstituo, isMenu, ...others } = props;
		const item = findItem(props.id);
		const arrowDir = expanded ? "k-i-arrow-chevron-down" : "k-i-arrow-chevron-right";
		let isVisible = isMenuItemVisible(props);
		let className = "constituo-nav-item";
		if (isChildSelected(item) || isParentSelected(item)) className = "constituo-nav-item-submenu-selected";
		if (isSelected(item)) className = "constituo-nav-item-selected";
		className += " constituo-nav-item-selectable";
		
		return (
			<div className={"constituo-container"} onContextMenu={handleContextMenu}>
				{isVisible === false ? null
					: (
						<div className="constituo-nav-item-wrapper">
							<DrawerItem 
								{...others} 
								className={className} 
								tabIndex={-1}
								aria-label={"Navigate to " + item.text}
							>
								{parentId != undefined && (<span className={"k-icon"} />)}
								<span className={"k-icon " + item.icon} />
								{ isMenu && <span className={"k-item-text"} onClick={(e) => menuItemClicked(e, item.id)}>{item.text}</span> }
								{ !isMenu && <span className={"k-item-text"} route={item.route}>{item.text}</span> }
								
								{ item.id === 'filtered' && isNavExpanded && filterState.searching && (
									<span
										className={"k-icon fa-fa-small fa-cs-logo fa-spin"}
										style={{ width: "40px", margin: "0px 0px 0px 10px" }}
									/>
								)}
								
								{expanded !== undefined && (menuState.expanded || menuState.temporarilyExpanded) && (
									<span
										className={"k-icon " + arrowDir}
										style={{
											position: "absolute",
											right: 10,
										}}
									/>
								)}
							</DrawerItem>
						
							{ item.id === 'filtered' && isNavExpanded && !filterState.searching && (
								<button
								    type="button"
									className={"constito-left-nav-filter-button constituo-left-nav-edit-filters-button k-icon fa fa-small fa-pencil"}
									style={{ width: "40px", margin: "0px 0px 0px 20px" }}
									role="button"
									title="Edit Filters"
									tabIndex="0"
									aria-label={"Edit Filters"}
									onClick={handleFilterClicked}
								/> 
							)}
							{ item.id === 'filtered' && isNavExpanded && !filterState.searching && hasFilters() && (
								<button
								    type="button"
									className={"constito-left-nav-filter-button constituo-left-nav-clear-filters-button k-icon fa fa-small fa-cancel"}
									style={{ width: "40px", margin: "0px 0px 0px 0px" }}
									role="button"
									title="Clear Filters"
									tabIndex="0"
									aria-label={"Clear Filters"}
									onClick={handleFiltersCleared}
								/> 
							)}
						</div>
					)				
				}
			{/*contextMenu()*/}			
			</div>
		);
	};
		
	const isChildSelected = (item) => {
		if (item.parentId != null) return false; // not a parent
		let children = findItemsByParent(item.id);
		for (let i = 0; i < children.length; i++) 
			if (isSelected(children[i])) return true;
		return false;
	};
	
	const isParentSelected = (item) => {
		if (item.parentId != null) 
			return isChildSelected(findItem(item.parentId));
		else
			return false; // not a child
	};
	
	const isSelected = (item) => {
		return path.startsWith(item.route);
	};
	
	const hasFilters = () => {
		return filterState.filters && JSON.parse(filterState.filters).length > 0
	}
	
	useEffect(() => {
		if (hasFilters()) 
			applyFilters(filterState.filters);
		else 
			updateMenuItems(items);
	}, []);
	
  	const data = items.map((item) => {
		const { parentId, ...others } = item;
		if (parentId !== undefined) {
			const parent = findItem(parentId);
			return { ...others, parentId: parentId, visible: parent.expanded };
		}
		return item;
	});

	return (
		<div className='main-view'>
			{ unsavedChangesDialogOptions.show && (
				<UnsavedChangesDialog 
					yesCallback={unsavedChangesConfirmed}
					noCallback={() => setUnsavedChangesDialogOptions({ show: false })}
				/>
			) }
			{ filterState.show && (
				<FiltersDialog
					filters={filterState.filters}
					okCallback={filtersUpdated}
					cancelCallback={() => setFilterState({ ...filterState, show: false })}
				/>
			) }
			<MainAppBarView 
				handleNavToggle={handleNavToggle} 
				navigateTo={navigateTo}
				sessionChanging={() => setSessionChanging(true)}
				sessionChanged={() => setSessionChanging(false)}
				messageInfo={messageInfo}
				hasFilters={hasFilters}
				handleFilterClicked={handleFilterClicked}
				handleFiltersCleared={handleFiltersCleared}
			/>
			{ sessionChanging && ( <LoadingPanel /> )}
			{ !sessionChanging && (
				<Drawer
					aria-label={"Left Navigation"}
					tabIndex={0}
					role="navigation"
					className={"constituo-container"}
					position="start"
					expanded={menuState.expanded || menuState.temporarilyExpanded}
					mode={matches ? "push" : "overlay"}
					mini={matches}
					animation={{ duration: 400 }}
					width={250}
					items={data}
					item={customItem}
					onOverlayClick={handleNavToggle}
					onSelect={(e) => handleSelect(e)} 
				>
					<DrawerContent
						role="main"
						className="constituo-content-container"
					>
						{props.children}				
					</DrawerContent>
					
					{contextMenu()}	
				</Drawer>				
			) }
		</div>
	);
}